import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Settings } from 'app/settings.class';
import { CodaltComponent } from '../codalt.component';
import { Customer } from '../customer';
import { CustomerProjectDialogComponent } from '../customer-project-manage/customer-project-dialog/customer-project-dialog.component';
import { CustomerProjectType } from '../customer-project-type.enum';
import { CustomerProject } from '../customer-project.interface';
import { AuthorisationService } from '../services/auth/authorisation.service';
import { ConfirmDialogService } from '../services/confirm-dialog-service/confirm-dialog.service';
import { CustomerProjectService } from '../services/customer-project/customer-project.service';
import { CustomerService } from '../services/customer/customer.service';
import { PageTitleService } from '../services/page-title/page-title.service';
import { SearchService } from '../services/search/search.service';

@Component({
    selector: 'app-customer-project-overview',
    templateUrl: './customer-project-overview.component.html',
    styleUrls: ['./customer-project-overview.component.scss'],
    standalone: false,
})
export class CustomerProjectOverviewComponent extends CodaltComponent implements OnInit, OnDestroy {
    @ViewChild('projectFilterSearchTemplate', { static: true }) projectFilterSearchTemplate;
    projectFilter: (CustomerProjectType | 'archive')[] = [CustomerProjectType.regular, CustomerProjectType.maintenance];

    customer: Customer;
    customerId: number;
    projects: CustomerProject[];
    filteredProjects: CustomerProject[];
    projectsCounts: Record<number, { name: string; count: number; colorcode: string }[]>;
    pendingAccessCounts: Record<number, number> = {};

    viewOnyDrilling: boolean;

    constructor(
        private customerService: CustomerService,
        private customerProjectService: CustomerProjectService,
        private router: Router,
        private search: SearchService,
        private searchService: SearchService,
        private confirmDialog: ConfirmDialogService,
        private route: ActivatedRoute,
        private pageTitleService: PageTitleService,
        private dialog: MatDialog
    ) {
        super();
    }

    ngOnInit(): void {
        setTimeout(() => (this.searchService.searchTemplate = this.projectFilterSearchTemplate));
        this.watchRouteParams(this.RouteParamnames.customerId, ({ customerId }) => {
            this.viewOnyDrilling = this.Utils.isDrillingRoute(this.route);
            this.pageTitleService.setTitle(this.viewOnyDrilling ? 'Booropdrachten' : 'Projecten');
            if (this.viewOnyDrilling) {
                this.projectFilter = [CustomerProjectType.drillings];
            }
            this.customerId = +customerId;
            if (!isNaN(this.customerId)) {
                this.subscriptions.add(
                    this.customerService.getSingle(this.customerId).subscribe(customer => {
                        this.customer = customer;
                        this.LocalStorage.setCustomer(customer);
                        this.pageTitleService.setTitle(
                            this.viewOnyDrilling ? 'Booropdrachten' : 'Projecten',
                            customer.customer_name
                        );
                        this.loadProjects();
                    })
                );
            }
        });
        this.subscriptions.add(
            this.search.change.subscribe(
                searchTerm =>
                    (this.filteredProjects = this.projects?.filter(
                        p => p.name?.toLowerCase().indexOf(searchTerm) !== -1
                    ))
            )
        );
    }

    loadProjects() {
        const allProjectTypes = Settings.projectTypes.map(pt => pt.key);
        const projectType = this.projectFilter.filter(f =>
            allProjectTypes.includes(f as CustomerProjectType)
        ) as CustomerProjectType[];
        const archive = !!this.projectFilter.find(f => f === 'archive');
        this.subscriptions.add(
            this.customerProjectService
                .getProjects(false, this.customerId, projectType, archive)
                .subscribe(projects => {
                    this.projects = projects;
                    this.filteredProjects = this.projects;
                })
        );

        if (AuthorisationService.hasFeature(this.Features.CustomerProjectsCounts)) {
            this.subscriptions.add(
                this.customerProjectService
                    .getProjectsWorkareaCounts()
                    .subscribe(counts => (this.projectsCounts = counts))
            );
        }

        if (AuthorisationService.hasFeature(this.Features.CustomerProjectsPendingAccessCounts)) {
            this.subscriptions.add(
                this.customerProjectService
                    .getPendingAccessCounts(this.customerId)
                    .subscribe(counts => (this.pendingAccessCounts = counts.per_project))
            );
        }
    }

    ngOnDestroy() {
        this.searchService.searchTemplate = null;
        super.ngOnDestroy();
    }

    addProject() {
        CustomerProjectDialogComponent.open(
            this.dialog,
            this.customer,
            Settings.projectTypes.filter(t => !this.viewOnyDrilling || t.key === CustomerProjectType.drillings)
        )
            .afterClosed()
            .subscribe(created => {
                if (created) {
                    this.router.navigate([created.id], { relativeTo: this.route });
                }
            });
    }

    archive() {
        this.confirmDialog
            .confirm(
                'Klantgebied archiveren',
                `Weet je zeker dat je het klantgebied <b>${this.customer.name}</b> wilt archiveren? Je doet dit voor alle gebruikers, dit kan niet ongedaan gemaakt worden.`,
                'Archiveren',
                'Behouden'
            )
            .then(() =>
                this.subscriptions.add(
                    this.customerService.archiveCustomer(this.customerId).subscribe(
                        () => {
                            this.customerService.customers = [];
                            this.router.navigate(['/', this.Routenames.customers]);
                        },
                        error => {
                            const err = error?.error?.error?.errormessage || error?.error?.errormessage || '';
                            this.confirmDialog.confirm('Oeps...', `Er is iets fout gegaan op de server ${err}`, 'Oke');
                        }
                    )
                )
            );
    }
}
